import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FcGoogle } from 'react-icons/fc';
import { Bell, Search, Settings, Star, User, LogOut, Menu } from 'lucide-react';

const TopBar = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  // Get toggleSidebar from Layout context
  const context = useOutletContext();
  const toggleSidebar = context?.toggleSidebar;

  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    return names.map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getUserAvatar = () => {
    return user?.picture || null;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };

    if (showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileDropdown]);

  const handleProfileClick = () => {
    setShowProfileDropdown(!showProfileDropdown);
  };

  const handleDropdownItemClick = (action) => {
    setShowProfileDropdown(false);
    
    switch(action) {
      case 'profile':
        navigate('/settings');
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'logout':
        logout();
        break;
      default:
        break;
    }
  };

  return (
    <div className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        {/* Left Side */}
        <div className="flex items-center gap-3 lg:gap-4 flex-1 min-w-0">
          {/* Hamburger Menu for Mobile */}
          <button
            onClick={toggleSidebar}
            className="lg:hidden w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
            aria-label="Toggle menu"
          >
            <Menu className="w-6 h-6 text-slate-700" />
          </button>
          
          <FcGoogle className="w-7 h-7 lg:w-9 lg:h-9 flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-base lg:text-xl font-bold text-slate-900 truncate">Google Review Responder</h1>
            <p className="text-xs text-slate-500 hidden sm:block">Automate Google Reviews Integration</p>
          </div>
          <div className="hidden md:flex items-center gap-2 bg-yellow-50 px-3 py-1.5 rounded-full border border-yellow-200 flex-shrink-0">
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
            <span className="text-sm font-bold text-slate-700">4.8</span>
            <span className="text-xs text-slate-600">Reviews</span>
          </div>
        </div>

        {/* Right Side - Only show after connection */}
        {isAuthenticated && (
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/search')}
              className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-all duration-200" 
              title="Search"
            >
              <Search className="w-5 h-5 text-slate-600" />
            </button>
            <button 
              onClick={() => navigate('/notifications')}
              className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-all duration-200 relative" 
              title="Notifications"
            >
              <Bell className="w-5 h-5 text-slate-600" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            </button>
            <button 
              onClick={() => navigate('/settings')}
              className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-all duration-200" 
              title="Settings"
            >
              <Settings className="w-5 h-5 text-slate-600" />
            </button>
            
            {/* Profile Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={handleProfileClick}
                className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:ring-2 hover:ring-blue-400"
                title="Profile"
              >
                {getUserAvatar() ? (
                  <img 
                    src={getUserAvatar()} 
                    alt="Profile" 
                    className="w-10 h-10 rounded-full border-2 border-blue-400"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-indigo-600 flex items-center justify-center text-white font-semibold">
                    {getUserInitials()}
                  </div>
                )}
              </button>

              {/* Dropdown Menu */}
              {showProfileDropdown && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50">
                  <button
                    onClick={() => handleDropdownItemClick('profile')}
                    className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center gap-3 transition-colors"
                  >
                    <User className="w-5 h-5 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900">My Profile</span>
                  </button>
                  <button
                    onClick={() => handleDropdownItemClick('settings')}
                    className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center gap-3 transition-colors"
                  >
                    <Settings className="w-5 h-5 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900">Settings</span>
                  </button>
                  <div className="border-t border-slate-200 my-2"></div>
                  <button
                    onClick={() => handleDropdownItemClick('logout')}
                    className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 transition-colors"
                  >
                    <LogOut className="w-5 h-5 text-red-600" />
                    <span className="text-sm font-medium text-red-600">Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopBar;
